import type { LineBatch } from './geometry.ts';
import type { Mat4 } from './mat4.ts';

/**
 * A small WebGPU line renderer.
 *
 * One pipeline, one shader, two draw calls. The whole scene is hairlines, so
 * there is nothing to sort and nothing to shade - which means the interesting
 * decisions are all about legibility rather than lighting:
 *
 * - 4x multisampling, because a one-pixel line without it crawls badly as the
 *   camera orbits.
 * - Depth testing off, painter's order on. Wireframes have no interiors to
 *   occlude, and depth-testing translucent lines against each other produces
 *   order-dependent gaps for no benefit.
 * - Distance fade instead, which restores the depth cue that dropping the
 *   depth buffer costs, and does it the way a draughtsman would.
 */

const SHADER = /* wgsl */ `
struct Uniforms {
  viewProjection : mat4x4<f32>,
  scale          : f32,
  alpha          : f32,
  fadeNear       : f32,
  fadeFar        : f32,
};

@group(0) @binding(0) var<uniform> u : Uniforms;

struct VertexOut {
  @builtin(position) position : vec4<f32>,
  @location(0)       colour   : vec4<f32>,
};

@vertex
fn vertexMain(
  @location(0) position : vec3<f32>,
  @location(1) colour   : vec4<f32>,
) -> VertexOut {
  var out : VertexOut;
  out.position = u.viewProjection * vec4<f32>(position * u.scale, 1.0);

  // Clip-space w equals view-space depth under a perspective projection, so
  // it doubles as the distance to fade against - no second matrix needed.
  let depth = out.position.w;
  let fade  = 1.0 - clamp((depth - u.fadeNear) / max(u.fadeFar - u.fadeNear, 0.001), 0.0, 1.0);
  let dimmed = mix(0.28, 1.0, fade);

  out.colour = vec4<f32>(colour.rgb, colour.a * u.alpha * dimmed);
  return out;
}

@fragment
fn fragmentMain(in : VertexOut) -> @location(0) vec4<f32> {
  // Premultiplied, to match the canvas alpha mode.
  return vec4<f32>(in.colour.rgb * in.colour.a, in.colour.a);
}
`;

/** 256-byte alignment is the minimum dynamic-offset stride WebGPU guarantees. */
const UNIFORM_STRIDE = 256;
const SAMPLE_COUNT = 4;

export interface DrawCall {
  batch: GpuBatch;
  /** Uniform scale applied in the vertex shader. */
  scale: number;
  /** Multiplies every vertex alpha. */
  alpha: number;
}

export interface GpuBatch {
  buffer: GPUBuffer;
  vertexCount: number;
  destroy(): void;
}

export class LineRenderer {
  private readonly context: GPUCanvasContext;
  private readonly pipeline: GPURenderPipeline;
  private readonly uniformBuffer: GPUBuffer;
  private readonly bindGroup: GPUBindGroup;
  private readonly uniformScratch = new Float32Array(20);

  private depthTexture: GPUTexture | null = null;
  private msaaTexture: GPUTexture | null = null;
  private width = 0;
  private height = 0;

  readonly device: GPUDevice;
  readonly canvas: HTMLCanvasElement;
  private readonly maxDraws: number;

  private constructor(
    device: GPUDevice,
    canvas: HTMLCanvasElement,
    context: GPUCanvasContext,
    format: GPUTextureFormat,
    maxDraws: number,
  ) {
    this.device = device;
    this.canvas = canvas;
    this.maxDraws = maxDraws;
    this.context = context;

    const module = device.createShaderModule({ code: SHADER, label: 'chirpr-lines' });
    const bindGroupLayout = device.createBindGroupLayout({
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: 'uniform', hasDynamicOffset: true, minBindingSize: 80 },
        },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      label: 'chirpr-line-pipeline',
      layout: device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] }),
      vertex: {
        module,
        entryPoint: 'vertexMain',
        buffers: [
          {
            arrayStride: 7 * 4,
            attributes: [
              { shaderLocation: 0, offset: 0, format: 'float32x3' },
              { shaderLocation: 1, offset: 12, format: 'float32x4' },
            ],
          },
        ],
      },
      fragment: {
        module,
        entryPoint: 'fragmentMain',
        targets: [
          {
            format,
            blend: {
              color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha' },
              alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha' },
            },
          },
        ],
      },
      primitive: { topology: 'line-list' },
      depthStencil: {
        format: 'depth24plus',
        depthWriteEnabled: false,
        depthCompare: 'always',
      },
      multisample: { count: SAMPLE_COUNT },
    });

    this.uniformBuffer = device.createBuffer({
      size: UNIFORM_STRIDE * maxDraws,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.bindGroup = device.createBindGroup({
      layout: bindGroupLayout,
      entries: [{ binding: 0, resource: { buffer: this.uniformBuffer, size: 80 } }],
    });
  }

  static async create(canvas: HTMLCanvasElement, maxDraws = 24): Promise<LineRenderer> {
    if (!navigator.gpu) throw new Error('This browser has no WebGPU.');
    const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'low-power' });
    if (!adapter) throw new Error('No WebGPU adapter is available.');
    const device = await adapter.requestDevice();
    const context = canvas.getContext('webgpu');
    if (!context) throw new Error('Could not create a WebGPU canvas context.');

    const format = navigator.gpu.getPreferredCanvasFormat();
    context.configure({ device, format, alphaMode: 'premultiplied' });
    return new LineRenderer(device, canvas, context, format, maxDraws);
  }

  upload(batch: LineBatch, label?: string): GpuBatch {
    const buffer = this.device.createBuffer({
      label,
      size: Math.max(batch.data.byteLength, 4),
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
    if (batch.data.byteLength > 0) this.device.queue.writeBuffer(buffer, 0, batch.data.buffer as ArrayBuffer, batch.data.byteOffset, batch.data.byteLength);
    return {
      buffer,
      vertexCount: batch.vertexCount,
      destroy: () => buffer.destroy(),
    };
  }

  /** Match the drawing buffer to the CSS box, capped so huge DPRs stay cheap. */
  resize(): { width: number; height: number; changed: boolean } {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round((this.canvas.clientWidth || 640) * ratio));
    const height = Math.max(1, Math.round((this.canvas.clientHeight || 360) * ratio));
    if (width === this.width && height === this.height && this.depthTexture) {
      return { width, height, changed: false };
    }

    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;

    this.depthTexture?.destroy();
    this.msaaTexture?.destroy();
    this.depthTexture = this.device.createTexture({
      size: [width, height],
      format: 'depth24plus',
      sampleCount: SAMPLE_COUNT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });
    this.msaaTexture = this.device.createTexture({
      size: [width, height],
      format: navigator.gpu.getPreferredCanvasFormat(),
      sampleCount: SAMPLE_COUNT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });
    return { width, height, changed: true };
  }

  render(
    viewProjection: Mat4,
    draws: DrawCall[],
    options: { clear: [number, number, number, number]; fadeNear: number; fadeFar: number },
  ): void {
    if (!this.depthTexture || !this.msaaTexture) this.resize();
    if (!this.depthTexture || !this.msaaTexture) return;

    const count = Math.min(draws.length, this.maxDraws);
    for (let i = 0; i < count; i++) {
      this.uniformScratch.set(viewProjection, 0);
      this.uniformScratch[16] = draws[i].scale;
      this.uniformScratch[17] = draws[i].alpha;
      this.uniformScratch[18] = options.fadeNear;
      this.uniformScratch[19] = options.fadeFar;
      this.device.queue.writeBuffer(
        this.uniformBuffer,
        i * UNIFORM_STRIDE,
        this.uniformScratch.buffer as ArrayBuffer,
        this.uniformScratch.byteOffset,
        this.uniformScratch.byteLength,
      );
    }

    const [r, g, b, a] = options.clear;
    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: this.msaaTexture.createView(),
          resolveTarget: this.context.getCurrentTexture().createView(),
          // Premultiplied clear, so a translucent page background shows through.
          clearValue: { r: r * a, g: g * a, b: b * a, a },
          loadOp: 'clear',
          storeOp: 'discard',
        },
      ],
      depthStencilAttachment: {
        view: this.depthTexture.createView(),
        depthClearValue: 1,
        depthLoadOp: 'clear',
        depthStoreOp: 'discard',
      },
    });

    pass.setPipeline(this.pipeline);
    for (let i = 0; i < count; i++) {
      const draw = draws[i];
      if (draw.batch.vertexCount === 0 || draw.alpha <= 0.001) continue;
      pass.setBindGroup(0, this.bindGroup, [i * UNIFORM_STRIDE]);
      pass.setVertexBuffer(0, draw.batch.buffer);
      pass.draw(draw.batch.vertexCount);
    }
    pass.end();

    this.device.queue.submit([encoder.finish()]);
  }

  destroy(): void {
    this.depthTexture?.destroy();
    this.msaaTexture?.destroy();
    this.uniformBuffer.destroy();
    this.device.destroy();
  }
}
